import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ArrowRight, BookOpen, Calendar, FlaskConical, Brain, Code2, Server, BarChart3, Users, Shield, Star, CheckCircle, Globe, Zap, Award, HeartHandshake, Award as AwardIcon } from "lucide-react";

function useReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return { ref, visible };
}

const stats = [
  { value: "10M+", label: "Individuals Trained" },
  { value: "800+", label: "Technology Courses" },
  { value: "80+", label: "Learning Experiences" },
  { value: "100+", label: "Training Experts" },
];

const platforms = [
  {
    id: "levelup", name: "LevelUp", tag: "SELF-PACED LEARNING",
    tagline: "Learn at your own pace, on your own schedule",
    desc: "Microsoft technology, on-demand learning platform built exclusively for Microsoft Partners. Access the full FY26 curriculum — anytime, anywhere.",
    features: ["Microsoft technology content", "Full FY26 course catalog", "Progress tracking & certifications", "Available globally, 24/7"],
    href: "https://skillupwithlevelup.com/", external: true,
    gradient: "from-[#194449] to-[#091F2C]", icon: BookOpen,
  },
  {
    id: "eventsup", name: "EventsUp", tag: "LIVE EVENTS & TRAINING",
    tagline: "End-to-end event management powered by Cvent",
    desc: "A fully managed event platform for live IT training, conferences, and virtual sessions. From registration to post-event reporting — we handle everything.",
    features: ["Powered by Cvent", "Custom event website creation", "Automated email communication", "Real-time attendance reporting"],
    href: "/platforms#eventsup", external: false,
    gradient: "from-[#091F2C] to-[#194449]", icon: Calendar,
  },
  {
    id: "stepup", name: "StepUp", tag: "SIMULATED LABS",
    tagline: "Hands-on lab experience for Microsoft courses",
    desc: "Project-ready technical skilling through immersive, simulated lab environments. Practice real-world Microsoft scenarios with the latest technologies.",
    features: ["Microsoft technology labs", "Latest Azure & M365 scenarios", "Project-ready skill building", "Self-paced lab access"],
    href: "https://stepup.technofocus.ai/", external: true,
    gradient: "from-[#07641D] to-[#194449]", icon: FlaskConical,
  },
];

const solutionAreas = [
  {
    id: "azure-cloud-ai", icon: Brain, title: "Azure — Cloud & AI Platform", subtitle: "Build AI-Ready Organizations",
    color: "#194449", bgColor: "#E5F3F0",
    desc: "Master the complete Microsoft Azure AI stack — from Azure OpenAI and Microsoft Fabric to AI Apps and Agents — with Cert Ready, Project Ready with Labs, and Tech Deal Ready courses.",
    topics: ["Innovate with Azure AI Apps and Agents", "Unify your Data Platform with Microsoft Fabric", "Migrate and Modernize your estate on Azure", "AZ-104, AZ-305, AZ-900 certifications"],
  },
  {
    id: "ai-business-solutions", icon: Code2, title: "AI Business Solutions (ABS)", subtitle: "Dynamics 365 & Power Platform + AI",
    color: "#07641D", bgColor: "#F2FBDB",
    desc: "The largest solution area with 222+ courses. Dynamics 365, Power Platform, and Microsoft Copilot for sales, technical, and presales roles.",
    topics: ["Copilot for Dynamics 365", "Copilot & Agents in Power Platform", "MCEM for Partners", "PL-900, PL-600, MB-210 certifications"],
  },
  {
    id: "security", icon: Shield, title: "Security", subtitle: "Zero Trust & Modern SecOps",
    color: "#0B2E38", bgColor: "#E5F3F0",
    desc: "83 courses spanning Data Security, Modern SecOps, and Cloud AI Protection — including 16-hour Project Ready with Labs deep-dives.",
    topics: ["Defender for Endpoint (12h lab)", "Threat Protection with Defender XDR", "Microsoft Entra Identity & Access (16h)", "SC-200, SC-300, SC-400 certifications"],
  },
  {
    id: "deal-ready", icon: BarChart3, title: "Deal Ready Accelerators", subtitle: "Presales Demo Environments",
    color: "#194449", bgColor: "#E5F3F0",
    desc: "Ready-to-demo Microsoft technology architectures for presales teams. Deploy complex scenarios with zero engineering overhead.",
    topics: ["Pre-built architecture scenarios", "Multilingual guided walkthroughs", "Built-in cost transparency", "Reusable across your sales team"],
  },
  {
    id: "ai-workforce", icon: Users, title: "AI Workforce", subtitle: "Microsoft 365 Copilot & AI Adoption",
    color: "#07641D", bgColor: "#F2FBDB",
    desc: "Build an AI-ready workforce with Microsoft 365 Copilot, AI agents, and Viva. Courses for every role from end-user to admin.",
    topics: ["Microsoft 365 Copilot adoption", "Copilot for Sales, Service & Finance", "AI Agents with Copilot Studio", "Secure AI productivity with M365"],
  },
  {
    id: "cert-prep", icon: Award, title: "Comprehensive Cert Prep", subtitle: "Azure, ABS, Security & Sales Certs",
    color: "#0B2E38", bgColor: "#E5F3F0",
    desc: "401+ Cert Ready courses for the full Microsoft technology certification portfolio — practice tests, study guides, and exam-mapped content.",
    topics: ["AZ-900, AZ-104, AZ-305 (Azure)", "PL-900, PL-600, PL-300 (Power Platform)", "SC-200, SC-300, SC-400 (Security)", "Sales & pre-sales role certifications"],
  },
];

const testimonials = [
  { name: "Samir Ahmed", role: "Tech Deal Ready Student", quote: "Thanks for the great exam practice course. I passed PL-900 today with 880! I will enroll for another course soon." },
  { name: "Avier Stine", role: "Enablement Lead", quote: "Simply the best and most skilled learning partner in Microsoft cloud!" },
  { name: "Gary Franklin", role: "Project Ready Course Student", quote: "Finally a course in which the lab instructions work accurately. Thanks for the expert guidance throughout the course." },
  { name: "Andrew Simon", role: "Sales-Ready Course Student", quote: "Thanks for such a well-organized learning experience. I will tell my colleagues about it." },
];

const features = [
  { icon: Globe, title: "Microsoft Cloud Center of Excellence", desc: "We help organizations establish a CoE focused on Microsoft cloud adoption, driving transformation and maximizing ROI." },
  { icon: Zap, title: "Accelerated New Hire Onboarding", desc: "Equip new hires with essential Microsoft technology skills faster, reducing ramp-up time and boosting productivity." },
  { icon: Award, title: "Sales & Pre-Sales Enablement", desc: "Empower your sales teams with deep technical knowledge to accelerate revenue and build stronger partner relationships." },
  { icon: HeartHandshake, title: "Multilingual Training Delivery", desc: "We deliver expert training in your team's native language, ensuring deeper understanding and better knowledge retention." },
];

const clients = [
  { name: "Microsoft", src: "/logo-microsoft.svg", h: 22 },
  { name: "KPMG", src: "/logo-kpmg.svg", h: 24 },
  { name: "EY", src: "/logo-ey-new.png", h: 38 },
  { name: "IBM", src: "/logo-ibm.svg", h: 24 },
  { name: "Infosys", src: "/logo-infosys.svg", h: 26 },
  { name: "Amdocs", src: "/logo-amdocs.png", h: 24 },
];

export default function Home() {
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [activeArea, setActiveArea] = useState(null);
  const statsSection = useReveal();
  const platformsSection = useReveal();
  const servicesSection = useReveal();
  const featuresSection = useReveal();

  useEffect(() => {
    const timer = setInterval(() => setActiveTestimonial(i => (i + 1) % testimonials.length), 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="overflow-x-hidden" style={{ backgroundColor: '#FFF8F3' }}>
      {/* ── HERO ── */}
      <section
        data-testid="hero-section"
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        style={{ background: "linear-gradient(135deg, #091F2C 0%, #194449 55%, #1E5C60 100%)" }}
      >
        {/* Dot pattern */}
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(rgba(212,236,142,0.08) 1px, transparent 1px)',
          backgroundSize: '36px 36px'
        }}></div>
        {/* Radial glow */}
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse 80% 60% at 65% 0%, rgba(212,236,142,0.12) 0%, transparent 65%)" }}></div>
        {/* Floating blobs */}
        <div className="absolute top-24 right-12 w-72 h-72 rounded-full animate-float" style={{ background: "radial-gradient(circle, rgba(185,220,210,0.08) 0%, transparent 70%)" }}></div>
        <div className="absolute bottom-24 left-12 w-52 h-52 rounded-full animate-float delay-300" style={{ background: "radial-gradient(circle, rgba(212,236,142,0.06) 0%, transparent 70%)" }}></div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 py-32 text-center">
          {/* Partner badge */}
          <div className="inline-flex items-center gap-2.5 rounded-full px-5 py-2 mb-8 animate-fade-in" style={{ backgroundColor: 'rgba(212,236,142,0.1)', border: '1px solid rgba(212,236,142,0.3)' }}>
            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: '#D4EC8E' }}></span>
            <span className="text-xs font-bold uppercase tracking-[0.18em]" style={{ color: '#D4EC8E' }}>Microsoft Learning Partner</span>
          </div>

          {/* Headline */}
          <h1
            data-testid="hero-headline"
            className="text-5xl sm:text-6xl lg:text-7xl font-light text-white tracking-tight mb-6 animate-fade-in-up delay-100"
            style={{ fontFamily: 'Outfit, sans-serif', lineHeight: 1.08 }}
          >
            Empowering the<br />
            <span style={{ color: '#D4EC8E', fontWeight: 600 }}>Microsoft</span> Partner<br />
            Ecosystem
          </h1>

          {/* Subtext */}
          <p className="text-lg max-w-2xl mx-auto mb-10 animate-fade-in-up delay-200" style={{ color: 'rgba(255,255,255,0.72)', fontFamily: 'IBM Plex Sans, sans-serif' }}>
            Expert-led certification training across Azure Cloud & AI, AI Business Solutions, and Security — delivered by Microsoft alumni for the Microsoft community.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16 animate-fade-in-up delay-300">
            <a
              href="https://catalog.skillupwithlevelup.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-courses"
              className="px-8 py-4 rounded-full font-semibold transition-all duration-300 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl"
              style={{ backgroundColor: '#D4EC8E', color: '#091F2C' }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = '#BFD872'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = '#D4EC8E'}
            >
              Explore Courses <ArrowRight size={16} />
            </a>
            <Link
              to="/platforms"
              data-testid="hero-cta-platforms"
              className="border text-white hover:bg-white/10 px-8 py-4 rounded-full font-semibold transition-all duration-300 flex items-center justify-center gap-2"
              style={{ borderColor: 'rgba(255,255,255,0.3)' }}
            >
              Our Platforms
            </Link>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl mx-auto animate-fade-in-up delay-400">
            {stats.map((stat) => (
              <div key={stat.label} data-testid={`hero-stat-${stat.value.replace('+', '')}`} className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-white mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{stat.value}</div>
                <div className="text-xs" style={{ color: 'rgba(255,255,255,0.55)' }}>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronDown className="w-6 h-6" style={{ color: 'rgba(255,255,255,0.35)' }} />
        </div>
      </section>

      {/* ── TRUSTED BY ── */}
      <section data-testid="trusted-by-section" className="py-10 border-y bg-white overflow-hidden" style={{ borderColor: '#E2EBE9' }}>
        <p className="text-center text-xs font-bold uppercase tracking-[0.2em] mb-6" style={{ color: '#B5AFA5' }}>Trusted by industry leaders</p>
        <div className="relative">
          <div className="flex gap-16 items-center animate-marquee" style={{ width: 'max-content' }}>
            {[...clients, ...clients, ...clients].map((client, i) => (
              <img
                key={i}
                src={client.src}
                alt={client.name}
                style={{ height: client.h, width: 'auto', opacity: 0.55, flexShrink: 0 }}
                className="hover:opacity-100 transition-opacity duration-300"
              />
            ))}
          </div>
        </div>
      </section>

      {/* ── PLATFORMS ── */}
      <section ref={platformsSection.ref} data-testid="platforms-section" className="py-20 md:py-28" style={{ backgroundColor: '#FFF8F3' }}>
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className={`text-center mb-14 transition-all duration-700 ${platformsSection.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <p className="text-xs font-bold uppercase tracking-[0.2em] mb-3" style={{ color: '#194449' }}>Our Ecosystem</p>
            <h2 className="text-3xl md:text-4xl font-medium mb-4" style={{ fontFamily: 'Outfit, sans-serif', color: '#111827' }}>Three Platforms. One Mission.</h2>
            <p className="max-w-xl mx-auto" style={{ color: '#6B7280' }}>Explore the platforms we've built to support every stage of the Microsoft learning journey.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {platforms.map((p, i) => (
              <div
                key={p.id}
                data-testid={`platform-card-${p.id}`}
                className={`rounded-2xl overflow-hidden group transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl bg-white border ${platformsSection.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${i * 120}ms`, borderColor: '#E2EBE9' }}
              >
                <div className={`bg-gradient-to-br ${p.gradient} p-6 text-white`}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                      <p.icon size={20} className="text-white" />
                    </div>
                    <span className="text-white/60 text-[10px] font-bold uppercase tracking-[0.15em]">{p.tag}</span>
                  </div>
                  <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{p.name}</h3>
                  <p className="text-white/75 text-sm">{p.tagline}</p>
                </div>
                <div className="p-6">
                  <p className="text-gray-600 text-sm leading-relaxed mb-5">{p.desc}</p>
                  <ul className="space-y-2 mb-6">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-center gap-2 text-sm text-gray-700">
                        <CheckCircle size={14} style={{ color: '#194449' }} className="shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  {p.external ? (
                    <a href={p.href} target="_blank" rel="noopener noreferrer" data-testid={`platform-cta-${p.id}`}
                      className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-colors text-white"
                      style={{ backgroundColor: '#194449' }}
                      onMouseEnter={e => e.currentTarget.style.backgroundColor = '#0B2E38'}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor = '#194449'}>
                      Explore {p.name} <ArrowRight size={14} />
                    </a>
                  ) : (
                    <Link to={p.href} data-testid={`platform-cta-${p.id}`}
                      className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-colors text-white"
                      style={{ backgroundColor: '#194449' }}
                      onMouseEnter={e => e.currentTarget.style.backgroundColor = '#0B2E38'}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor = '#194449'}>
                      Explore {p.name} <ArrowRight size={14} />
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SOLUTION AREAS ── */}
      <section ref={servicesSection.ref} data-testid="services-section" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className={`text-center mb-14 transition-all duration-700 ${servicesSection.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <p className="text-xs font-bold uppercase tracking-[0.2em] mb-3" style={{ color: '#194449' }}>FY26 Solution Areas</p>
            <h2 className="text-3xl md:text-4xl font-medium mb-4" style={{ fontFamily: 'Outfit, sans-serif', color: '#111827' }}>What We Teach</h2>
            <p className="max-w-xl mx-auto" style={{ color: '#6B7280' }}>Six Microsoft technology solution areas — from foundational cert prep to deep technical implementation — spanning 550+ courses on LevelUp.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {solutionAreas.map((area, i) => (
              <div
                key={area.id}
                data-testid={`service-card-${i}`}
                className={`rounded-2xl border overflow-hidden group cursor-pointer hover:shadow-xl hover:-translate-y-1 transition-all duration-300 ${servicesSection.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${i * 80}ms`, borderColor: '#E2EBE9' }}
                onClick={() => setActiveArea(activeArea === area.id ? null : area.id)}
              >
                <div className="p-5" style={{ backgroundColor: area.bgColor }}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: area.color + '18' }}>
                      <area.icon size={20} style={{ color: area.color }} />
                    </div>
                    <span className="text-xs font-semibold px-2.5 py-1 rounded-full text-white" style={{ backgroundColor: area.color }}>
                      {activeArea === area.id ? 'Less' : 'Details'}
                    </span>
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-0.5" style={{ fontFamily: 'Outfit, sans-serif', fontSize: '0.95rem' }}>{area.title}</h3>
                  <p className="text-xs font-medium" style={{ color: area.color }}>{area.subtitle}</p>
                </div>
                <div className="p-5 bg-white">
                  <p className="text-gray-600 text-sm leading-relaxed mb-4">{area.desc}</p>
                  {activeArea === area.id && (
                    <ul className="space-y-1.5 mb-4">
                      {area.topics.map((t) => (
                        <li key={t} className="flex items-start gap-2 text-xs text-gray-700">
                          <CheckCircle size={12} className="shrink-0 mt-0.5" style={{ color: '#194449' }} />
                          {t}
                        </li>
                      ))}
                    </ul>
                  )}
                  <a href="https://catalog.skillupwithlevelup.com" target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs font-semibold hover:gap-1.5 transition-all"
                    style={{ color: area.color }}
                    onClick={e => e.stopPropagation()}>
                    View courses <ArrowRight size={11} />
                  </a>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link to="/services" data-testid="services-cta-button"
              className="inline-flex items-center gap-2 text-white px-8 py-3.5 rounded-full font-semibold transition-colors"
              style={{ backgroundColor: '#194449' }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = '#0B2E38'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = '#194449'}>
              Explore Full Catalog <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* ── STATS ── */}
      <section ref={statsSection.ref} data-testid="stats-section"
        style={{ background: 'linear-gradient(135deg, #091F2C, #194449, #1E5C60)' }}
        className="py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <div
                key={stat.label} data-testid={`stats-item-${i}`}
                className={`text-center transition-all duration-700 ${statsSection.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="text-4xl md:text-5xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{stat.value}</div>
                <div className="text-sm" style={{ color: 'rgba(255,255,255,0.55)' }}>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ABOUT PREVIEW ── */}
      <section data-testid="about-preview-section" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] mb-4" style={{ color: '#194449' }}>Our Story</p>
              <h2 className="text-3xl md:text-4xl font-medium mb-6" style={{ fontFamily: 'Outfit, sans-serif', color: '#111827' }}>
                Built by Microsoft Alumni,<br />Trusted by the Microsoft Community
              </h2>
              <p className="text-gray-600 leading-relaxed mb-5">
                Founded in 2011 by Ranjana Jain, a former Microsoft trainer, trainer-of-trainers, and technology evangelist, Technofocus was built with a singular vision: to deliver in-depth, scalable learning experiences that genuinely transform careers.
              </p>
              <p className="text-gray-600 leading-relaxed mb-8">
                Today, with a team of 100+ training delivery experts and a global footprint spanning Singapore and India, we've trained over 10 million professionals across the Microsoft ecosystem.
              </p>
              <Link to="/about" data-testid="about-cta-button"
                className="inline-flex items-center gap-2 text-white px-7 py-3.5 rounded-full font-semibold transition-colors"
                style={{ backgroundColor: '#194449' }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#0B2E38'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = '#194449'}>
                Our Full Story <ArrowRight size={16} />
              </Link>
            </div>
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/7693114/pexels-photo-7693114.jpeg"
                alt="Technofocus team"
                className="rounded-2xl w-full object-cover shadow-xl"
                style={{ height: 420 }}
              />
              <div className="absolute -bottom-5 -left-5 bg-white rounded-2xl shadow-xl p-5 border" style={{ borderColor: '#E2EBE9' }}>
                <div className="text-3xl font-bold mb-1" style={{ fontFamily: 'Outfit, sans-serif', color: '#194449' }}>2011</div>
                <div className="text-gray-500 text-sm">Year founded</div>
              </div>
              <div className="absolute -top-5 -right-5 rounded-2xl shadow-xl p-5 text-white"
                style={{ background: 'linear-gradient(135deg, #07641D, #194449)' }}>
                <div className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>2</div>
                <div className="text-sm opacity-80 mt-0.5">Global offices</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── KEY FEATURES ── */}
      <section ref={featuresSection.ref} data-testid="features-section" style={{ backgroundColor: '#FFF8F3' }} className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className={`text-center mb-14 transition-all duration-700 ${featuresSection.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <p className="text-xs font-bold uppercase tracking-[0.2em] mb-3" style={{ color: '#194449' }}>Why Technofocus</p>
            <h2 className="text-3xl md:text-4xl font-medium" style={{ fontFamily: 'Outfit, sans-serif', color: '#111827' }}>What Sets Us Apart</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((f, i) => (
              <div
                key={f.title} data-testid={`feature-card-${i}`}
                className={`p-6 bg-white rounded-2xl border hover:shadow-lg hover:-translate-y-1 transition-all duration-300 ${featuresSection.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${i * 80}ms`, borderColor: '#E2EBE9' }}
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: '#E5F3F0' }}>
                  <f.icon size={22} style={{ color: '#194449' }} />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2 text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>{f.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section data-testid="testimonials-section" className="py-20 md:py-28 bg-white">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] mb-3" style={{ color: '#194449' }}>Testimonials</p>
          <h2 className="text-3xl md:text-4xl font-medium mb-12" style={{ fontFamily: 'Outfit, sans-serif', color: '#111827' }}>What Our Learners Say</h2>
          <div className="relative">
            {testimonials.map((t, i) => (
              <div key={i} data-testid={`testimonial-${i}`}
                className={`transition-all duration-500 ${i === activeTestimonial ? 'opacity-100 block' : 'opacity-0 hidden'}`}>
                <div className="rounded-3xl p-10 border" style={{ backgroundColor: '#FFF8F3', borderColor: '#E2EBE9' }}>
                  <div className="flex justify-center gap-1 mb-6">
                    {[...Array(5)].map((_, si) => <Star key={si} size={16} fill="#07641D" style={{ color: '#07641D' }} />)}
                  </div>
                  <blockquote className="text-xl text-gray-700 italic leading-relaxed mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    "{t.quote}"
                  </blockquote>
                  <div>
                    <div className="font-semibold text-gray-900">{t.name}</div>
                    <div className="text-gray-400 text-sm">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
            <div className="flex justify-center gap-2 mt-6">
              {testimonials.map((_, i) => (
                <button
                  key={i} data-testid={`testimonial-dot-${i}`}
                  onClick={() => setActiveTestimonial(i)}
                  className={`h-2 rounded-full transition-all ${i === activeTestimonial ? 'w-6' : 'w-2 bg-gray-200'}`}
                  style={i === activeTestimonial ? { backgroundColor: '#194449', width: '1.5rem' } : {}}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}
